// Confirm Info page - allows creators to update their member info fields

import { MemberFieldForm } from "@/components/member-data";
import { ProjectPageHeader } from "@/components/projects/project-page-header";
import { captureEvent } from "@/lib/analytics-loader";
import {
  useMemberDataQuery,
  useUpdateMemberFieldsMutation,
  type MemberDataResponse,
} from "@/lib/queries/member-data";
import {
  useProjectInteractionsQuery,
  type CreatorInteraction,
} from "@/lib/queries/projects";
import { extractRequestId } from "@/lib/url-utils";
import { Card, CardContent, Skeleton, toast } from "@repo/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";
import type { CampaignRouteContext } from "@/lib/types/route-context";

export const Route = createFileRoute(
  "/(org)/o/$orgSlug/p/$profileSlug/c/$campaignId/confirm-info/$interactionId",
)({
  component: ConfirmInfoPage,
});

// ============================================================================
// Presentational View (exported for Storybook)
// ============================================================================

export interface ConfirmInfoPageViewProps {
  isLoading: boolean;
  error: Error | null;
  memberData: MemberDataResponse | null;
  clientName: string;
  onSubmit: (fieldValues: Record<string, string | null>) => void;
  isSubmitting: boolean;
}

export function ConfirmInfoPageView({
  isLoading,
  error,
  memberData,
  clientName,
  onSubmit,
  isSubmitting,
}: ConfirmInfoPageViewProps): ReactNode {
  if (isLoading) {
    return <ConfirmInfoPageSkeleton />;
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 py-4 sm:py-6">
        <ProjectPageHeader title="Confirm Information" />
        <Card className="mt-4">
          <CardContent className="py-8 text-center">
            <p className="text-destructive">
              Failed to load your information. Please try again.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!memberData?.member || memberData.fields.length === 0) {
    return (
      <div className="px-4 sm:px-6 py-4 sm:py-6">
        <ProjectPageHeader title="Confirm Information" />
        <Card className="mt-4">
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">
              No member data found. Please contact support.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 py-4 sm:py-6 space-y-6">
      {/* Header */}
      <ProjectPageHeader
        title="Confirm Your Information"
        subtitle={
          <>
            Please verify and update your information
            {clientName ? ` for ${clientName}` : ""}
          </>
        }
      />

      {/* Dynamic field form */}
      <MemberFieldForm
        key={memberData.member.id}
        fields={memberData.fields}
        termsAccepted={memberData.termsAccepted}
        onSubmit={onSubmit}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}

// ============================================================================
// Container (hooks + data fetching)
// ============================================================================

function ConfirmInfoPage() {
  const { interactionId } = Route.useParams();
  const context = Route.useRouteContext() as CampaignRouteContext;
  const { campaign, programId, profile, organization } = context;

  // Fetch interactions to get the secureUrl for this interaction
  const { data: interactionsData, isPending: interactionsPending } =
    useProjectInteractionsQuery(organization.id, programId, profile.id);

  // Find the specific interaction and extract the requestId from its secureUrl
  const interaction = interactionsData?.interactions.find(
    (i: CreatorInteraction) => i.id === interactionId,
  );
  const requestId = interaction?.secureUrl
    ? extractRequestId(interaction.secureUrl)
    : null;

  // Fetch member data using the requestId from the interaction's secureUrl
  const {
    data: memberData,
    isPending: memberDataPending,
    error,
  } = useMemberDataQuery(organization.id, requestId ?? "");

  const isPending = interactionsPending || (!!requestId && memberDataPending);

  // Update mutation
  const updateMutation = useUpdateMemberFieldsMutation(organization.id);
  const navigate = useNavigate();

  const handleSubmit = async (fieldValues: Record<string, string | null>) => {
    if (!memberData?.member) return;

    try {
      const result = await updateMutation.mutateAsync({
        profileId: profile.id,
        memberIds: [memberData.member.id],
        fields: fieldValues,
        requestId: requestId ?? undefined,
      });

      captureEvent("member_info_confirmed", {
        organizationId: organization.id,
        interactionId,
      });

      if (result.warning) {
        toast.warning(result.warning);
      } else {
        toast.success("Your information has been submitted successfully");

        // Navigate back to the project page so the task shows as completed.
        // Only navigate on success — when there's a warning the user stays
        // on the form so they can retry the submit.
        navigate({
          to: "/o/$orgSlug/p/$profileSlug/c/$campaignId",
          params: {
            orgSlug: organization.slug,
            profileSlug: campaign.profileSlug,
            campaignId: String(programId),
          },
        });
      }
    } catch {
      toast.error("Failed to submit your information. Please try again.");
    }
  };

  return (
    <ConfirmInfoPageView
      isLoading={isPending}
      error={error}
      memberData={memberData ?? null}
      clientName={memberData?.member?.client.name ?? ""}
      onSubmit={handleSubmit}
      isSubmitting={updateMutation.isPending}
    />
  );
}

function ConfirmInfoPageSkeleton() {
  return (
    <div className="px-4 sm:px-6 py-4 sm:py-6 space-y-6">
      <div className="flex items-center gap-4">
        {/* BackButton removed */}
        <div className="flex-1">
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-4 w-48" />
        </div>
      </div>

      <Card>
        <CardContent className="p-6 space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full mt-4" />
        </CardContent>
      </Card>
    </div>
  );
}
